/**
 * Loads Google Analytics 4 (gtag.js) on the web when VITE_GA4_MEASUREMENT_ID is
 * configured, and no-ops otherwise. Call once at app startup. After this runs,
 * `trackEvent()` in analytics.ts forwards events to the configured property.
 * WS-8 of the launch plan. No measurement id set → analytics stays inert.
 */
interface GtagWindow {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

export function initAnalytics(): void {
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!id) return;

  const w = window as unknown as GtagWindow;
  if (w.gtag) return; // already initialized

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  w.dataLayer = w.dataLayer ?? [];
  // gtag.js only processes commands pushed as the live `arguments` object — a
  // plain array is silently ignored (no page_view, no /g/collect). This must use
  // a classic function + `arguments`, matching Google's canonical gtag snippet.
  const gtag: (...args: unknown[]) => void = function () {
    // eslint-disable-next-line prefer-rest-params -- gtag.js requires the arguments object, not an array
    w.dataLayer!.push(arguments);
  };
  w.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id);
}
